import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./dashboard.css";
import { Box } from "@mui/material";
import { NavBar } from "../../../../components/NavBar/NavBar";
import { useNavigate } from "react-router-dom";
import { Tabs } from "../../../../components/DashboardTabs/Tabs";
import {
  selectors,
  actions,
} from "../../../../redux/features/dashboards/supplyChainDashboardSlice";
import { useTabsByWorkStreamQuery } from "../../../../hooks/useTabsByWorkStreamQuery";
import { useTabContentQuery } from "../../../../hooks/useTabContentQuery";
import { useAddDashboardTabMutation } from "../../../../hooks/useAddDashboardTabMutation";
import { useAddPanelToTabMutation } from "../../../../hooks/useAddPanelToTabMutation";
import { useUpdatePanelsPositionsMutation } from "../../../../hooks/useUpdatePanelsPositionsMutation";
import { useGridStackContext } from "../../../../contexts/grid-stack-context";
import { useDeleteDashboardPanelMutation } from "../../../../hooks/useDeleteDashboardPanelMutation";
import { useDeleteDashboardTabMutation } from "../../../../hooks/useDeleteDashboardTabMutation";
import { actions as loadingActions } from "../../../../redux/features/loader/loaderSlice";
import PanelFilter from "../controlPanel/PanelFilter";
import { ReduxLoader } from "../../../../components/Loader/ReduxLoader";
import { useUpdateDashboardTabMutation } from "../../../../hooks/useUpdateDashboardTabMutation";

const SupplyChainDashboardModule = ({ navBarInfo }) => {
  const dispatch = useDispatch();
  const { instance, events } = useGridStackContext();
  const activeTabId = useSelector(
    selectors.selectSupplyChainDashboardActiveTabId
  );
  const [isPanelFilterOpen, setIsPanelFilterOpen] = useState(false);

  const requestHandlers = {
    onLoading: loadingActions.setLoader,
    onError: actions.setError,
  };

  const tabs = useTabsByWorkStreamQuery({
    ...requestHandlers,
    stream: "supplyChain",
    selectors: {
      tabs: selectors.selectSupplyChainDashboardTabs,
      isLoading: selectors.selectIsSupplyChainDashboardLoading,
    },
    onResponse: ({ id, tabs }) => {
      dispatch(actions.setId(id));
      dispatch(actions.setTabs(tabs));
    },
  });

  const { TabContent, props } = useTabContentQuery({
    ...requestHandlers,
    stream: "supplyChain",
    selectors: {
      tabId: selectors.selectSupplyChainDashboardActiveTabId,
      content: selectors.selectSupplyChainDashboardTabContent,
      isLoading: selectors.selectIsSupplyChainDashboardLoading,
    },
    actions: {
      upgradeToGridstack: (...args) =>
        dispatch(actions.upgradePanelContentToGridStack(...args)),
    },
    onResponse: (panelContent) => {
      dispatch(actions.setTabContentById(panelContent));
      if (panelContent) {
        events.onPanelsFetch(panelContent);
      }
    },
  });

  const { addWorkStreamTab } = useAddDashboardTabMutation({
    ...requestHandlers,
    selectors: {
      id: selectors.selectSupplyChainDashboardId,
      tabs: selectors.selectSupplyChainDashboardTabs,
    },
    onResponse: actions.addTab,
  });

  const { deleteDashboardTab } = useDeleteDashboardTabMutation({
    ...requestHandlers,
    onResponse: actions.deleteTab,
  });

  const { updateDashboardTab } = useUpdateDashboardTabMutation({
    ...requestHandlers,
    selectors: {
      tabs: selectors.selectSupplyChainDashboardTabs,
    },
    onResponse: actions.updateTab,
  });

  const { addPanelInTab } = useAddPanelToTabMutation({
    ...requestHandlers,
    selector: selectors.selectSupplyChainDashboardActiveTabId,
    onResponse: (e) => dispatch(actions.upgradePanelContentToGridStack(e)),
  });

  const { deleteDashboardPanel } =
    useDeleteDashboardPanelMutation(requestHandlers);

  const { updatePositions } = useUpdatePanelsPositionsMutation({
    ...requestHandlers,
    selector: selectors.selectSupplyChainDashboardActiveTabId,
  });

  const onTabChange = (newtab) => {
    dispatch(actions.setActiveTabId(newtab));
  };

  const onTabReset = () => {
    dispatch(actions.resetTabContent());
  };

  const handleTogglePanelFilter = () => {
    setIsPanelFilterOpen(!isPanelFilterOpen);
  };

  const handleClosePanelFilter = () => {
    setIsPanelFilterOpen(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("mod_id", "11");
  }, []);

  useEffect(() => {
    events.set.onAddPanel(addPanelInTab);
    events.set.onUpdatePanel(updatePositions);
    events.set.onDeletePanel(deleteDashboardPanel);
    events.set.onResetTabContent(onTabReset);
  }, [activeTabId]);

  return (
    <div className="dashboard">
      <NavBar
        hiddeBackButton={true}
        hideBreadcrumbs={true}
        dataBreadCrumbs={navBarInfo.dataBreadCrumbs}
        moduleIcon={navBarInfo.moduleIcon}
        moduleColor={navBarInfo.moduleColor}
        title={navBarInfo.title}
        menuTitle={navBarInfo.menuTitle}
        moduleId={navBarInfo.moduleId}
      />

      <section
        className={`dashboard-container ${
          isPanelFilterOpen ? "with-filter-panel" : ""
        }`}
      >
        <Tabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={onTabChange}
          onAddTab={addWorkStreamTab}
          onDeleteTab={deleteDashboardTab}
          onUpdateTab={updateDashboardTab}
          onToggleFilterPanel={handleTogglePanelFilter}
        />
        <div className="dashboard-content-wrapper">
          <Box
            sx={{
              flexGrow: "1",
            }}
            className="box-style"
            role="tabpanel"
            id={`dashboard-${activeTabId}`}
            key={`dashboard-${activeTabId}`}
            aria-labelledby={`dashboard-${activeTabId}`}
          >
              <div ref={instance.ref} id="grid-stack-container-ref" />
              <ReduxLoader />
              <TabContent {...props} />
          </Box>
        </div>
        <PanelFilter
          open={isPanelFilterOpen}
          onClose={handleClosePanelFilter}
        />
      </section>
    </div>
  );
};

export default SupplyChainDashboardModule;
